import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';

interface FAQItem {
  q: string;
  a: string;
}

interface ArticleLayoutProps {
  title: string;
  slug: string;
  description: string;
  readTime: string;
  heroImageUrl: string;
  heroImageAlt: string;
  faqData: FAQItem[];
  children: ReactNode;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://mulebuyspreadsheets.beauty';

export default function ArticleLayout({
  title,
  slug,
  description,
  readTime,
  heroImageUrl,
  heroImageAlt,
  faqData,
  children,
}: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: heroImageUrl,
    datePublished: '2026-06-01',
    dateModified: '2026-06-22',
    author: {
      '@type': 'Organization',
      name: 'Mulebuy Spreadsheet Hub',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Mulebuy Spreadsheet Hub',
      url: SITE_URL,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/${slug}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title={`${title} — Mulebuy Spreadsheet Hub`}
        description={description}
        canonicalPath={`/${slug}`}
        ogType="article"
        ogImage={heroImageUrl}
        jsonLd={[articleSchema, faqSchema]}
      />
      <Navbar />
      <main>
        <article className="pt-[68px]">
          {/* Header */}
          <section className="bg-background-50 py-14 md:py-20">
            <div className="max-w-[720px] mx-auto px-6 md:px-0">
              <span className="inline-flex items-center px-3 py-1 rounded-lg bg-accent-100 text-accent-800 text-xs font-semibold mb-4 uppercase tracking-wide">
                SEO Article
              </span>

              <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-[1.15] mb-6">
                {title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8">
                <span>Updated June 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{readTime}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>By Spreadsheet Experts</span>
              </div>

              <div className="relative rounded-2xl overflow-hidden mb-10">
                <img
                  src={heroImageUrl}
                  alt={heroImageAlt}
                  className="w-full aspect-[16/9] object-cover"
                />
              </div>

              {/* Article Content */}
              <div className="prose-custom">
                {children}

                {/* Internal Links to Related Articles */}
                <div className="mt-8 p-6 bg-background-100 rounded-2xl">
                  <h4 className="font-heading text-base font-semibold text-foreground-900 mb-3">Related Mulebuy Spreadsheet Articles</h4>
                  <div className="flex flex-wrap gap-2">
                    <Link to="/mulebuy-spreadsheet-guide" className="text-xs text-accent-700 hover:text-accent-800 bg-accent-100/50 px-3 py-1.5 rounded-lg transition-colors">Ultimate Guide</Link>
                    <Link to="/how-mulebuy-spreadsheet-works" className="text-xs text-accent-700 hover:text-accent-800 bg-accent-100/50 px-3 py-1.5 rounded-lg transition-colors">How It Works</Link>
                    <Link to="/is-mulebuy-spreadsheet-safe" className="text-xs text-accent-700 hover:text-accent-800 bg-accent-100/50 px-3 py-1.5 rounded-lg transition-colors">Safety Analysis</Link>
                    <Link to="/complete-mulebuy-shopping-guide" className="text-xs text-accent-700 hover:text-accent-800 bg-accent-100/50 px-3 py-1.5 rounded-lg transition-colors">Shopping Guide</Link>
                    <Link to="/" className="text-xs text-accent-700 hover:text-accent-800 bg-accent-100/50 px-3 py-1.5 rounded-lg transition-colors">Home Hub</Link>
                  </div>
                </div>

                {/* FAQ Section */}
                <section className="mt-14 pt-10 border-t border-background-200/60">
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-8">
                    Frequently Asked Questions
                  </h2>

                  <div className="space-y-4">
                    {faqData.map((faq, idx) => (
                      <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden cursor-pointer">
                        <summary className="flex items-center justify-between px-5 py-4 text-base font-semibold text-foreground-900 hover:text-accent-700 transition-colors">
                          {faq.q}
                          <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 ml-3">
                            <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:hidden"></i>
                            <i className="ri-arrow-up-s-line text-lg text-accent-600 hidden group-open:block"></i>
                          </span>
                        </summary>
                        <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                          {faq.a}
                        </div>
                      </details>
                    ))}
                  </div>
                </section>

                {/* Bottom CTA */}
                <div className="mt-14 p-8 md:p-10 bg-background-100 rounded-2xl text-center">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-3">
                    Ready to Explore the Full Mulebuy Spreadsheet?
                  </h3>
                  <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto leading-relaxed">
                    Access the complete, daily-updated mulebuy spreadsheet hub and start discovering verified deals across all 14 product categories.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
                    >
                      <i className="ri-home-4-line text-sm"></i>
                      Back to Mulebuy Spreadsheet Hub
                    </Link>
                    <a
                      href="https://kaspreadsheet.com/"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-foreground-900 text-foreground-900 text-sm font-semibold whitespace-nowrap hover:bg-foreground-50 transition-colors cursor-pointer"
                    >
                      Buy It Now
                      <i className="ri-arrow-right-up-line text-sm"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}